'use client';

import { ReactNode, Suspense, useEffect, useState } from 'react';
import { ActivityOverlay } from '@rv-life/design-system';
import { getClientConfig, type ClientConfig } from '@/app/actions';
import { AppContextProvider } from './AppContext';
import { RouteAnalytics } from './RouteAnalytics';
import { initializeClientServices } from '../utils/init-client';
import { attachAnalyticsBridge } from '../utils/analytics';

interface ProvidersProps {
	children: ReactNode;
	hasSessionCookies: boolean;
}

/**
 * Providers component that wraps the application with all necessary context providers
 * and initializes client-side services. This component serves as the root provider
 * layer for the application, setting up the AppContext and handling initial client
 * service configuration.
 *
 * @param props - Component props
 * @param props.children - Child components to be wrapped by the providers
 *
 * @returns React.ReactElement containing the provider hierarchy with all child components
 *
 * Side Effects:
 * - useEffect hook runs once on mount (empty dependency array) to initialize client services
 * - Fetches client configuration via getClientConfig() and passes it to initializeClientServices()
 * - initializeClientServices() sets up client-side services like Bugsnag error tracking and Google Analytics
 *
 * Behavior:
 * - Wraps children in Suspense with ActivityOverlay as fallback for lazy-loaded components
 * - Provides AppContext to all child components
 */
export const Providers = ({ children, hasSessionCookies }: ProvidersProps): React.ReactElement => {
	const [analyticsStarted, setAnalyticsStarted] = useState(false);

	useEffect(() => {
		const startAnalytics = async () => {
			let config: ClientConfig;
			try {
				config = await getClientConfig();
			} catch (error) {
				// Bugsnag can't report here — it's only started once initializeClientServices succeeds.
				// Without the config's measurement id analytics stays off this session; log rather than
				// leave an unhandled rejection and a silent, undetectable gap.
				// eslint-disable-next-line no-console
				console.error(
					'Failed to load client config; analytics disabled this session',
					error
				);
				return;
			}

			try {
				initializeClientServices(config);
				setAnalyticsStarted(true);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Client analytics init failed; disabled this session', error);
			}
		};
		startAnalytics();
	}, []);

	// Translate shared-remote rvlife_analytics DOM events to GA4; emit() no-ops until GA is ready.
	useEffect(() => attachAnalyticsBridge(), []);

	return (
		<Suspense fallback={<ActivityOverlay />}>
			<AppContextProvider hasSessionCookies={hasSessionCookies}>
				<RouteAnalytics enabled={analyticsStarted} />
				{children}
			</AppContextProvider>
		</Suspense>
	);
};
